/* begin general import */
import React from "react";
import { Checkbox, Modal, Spin } from "antd";
import { ModalProps } from "components/Utility/Modal/Modal";
import { AppUser } from "models/AppUser";
import { Observable } from "rxjs";
import { AppUserRole } from "models/AppUserRole";
import { finalize } from "rxjs/operators";
import "./AppUserDetailRoleModal.scss";
/* end general import */

interface AppUserDetailRoleModalProps extends ModalProps {
  model: AppUser;
  loading: boolean;
  getRoles: () => Observable<AppUserRole[]>;
}

// Sửa vai trò của một tài khoản admin. Phải giữ ít nhất 1 vai trò mới lưu được.
function AppUserDetailRoleModal(props: AppUserDetailRoleModalProps) {
  const { model, loading, getRoles, visible, handleSave, handleCancel } = props;

  const [loadingRoles, setLoadingRoles] = React.useState<boolean>(false);
  const [roleOptions, setRoleOptions] = React.useState<AppUserRole[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);

  React.useEffect(() => {
    setLoadingRoles(true);
    const subscription = getRoles()
      .pipe(finalize(() => setLoadingRoles(false)))
      .subscribe((items: AppUserRole[]) => setRoleOptions(items ?? []));
    return () => subscription.unsubscribe();
  }, [getRoles]);

  // Mỗi lần mở lấy lại vai trò đang lưu: huỷ giữa chừng thì lần sau không còn lựa chọn dở.
  React.useEffect(() => {
    if (!visible) return;
    setSelectedIds(
      (model.appUserRoleMappings ?? [])
        .map((mapping) => mapping.role?.id ?? mapping.roleId)
        .filter((id) => id !== undefined && id !== null)
    );
  }, [visible, model.appUserRoleMappings]);

  const isEmpty = selectedIds.length === 0;

  const handleToggle = React.useCallback((roleId: number, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? [...current, roleId] : current.filter((id) => id !== roleId)
    );
  }, []);

  const handleRoleSave = React.useCallback(() => {
    if (isEmpty) return;
    // Ghi vai trò đã chọn vào model rồi lưu (giữ cách lưu cũ của hook).
    model.appUserRoleMappings = roleOptions
      .filter((role) => selectedIds.includes(role.id))
      .map((role) => ({
        errors: null,
        appUserId: model.id,
        roleId: role.id,
        role,
      }));
    handleSave();
  }, [handleSave, isEmpty, model, roleOptions, selectedIds]);

  return (
    <Modal
      className="role-modal"
      visible={visible}
      width={440}
      destroyOnClose
      onCancel={handleCancel}
      title={
        <div>
          <div className="role-modal__title">Sửa vai trò</div>
          <div className="role-modal__subtitle">
            {[model.displayName, model.email].filter(Boolean).join(" · ")}
          </div>
        </div>
      }
      footer={
        <div className="role-modal__footer">
          <button type="button" className="role-modal__btn" onClick={handleCancel}>
            Huỷ
          </button>
          <button
            type="button"
            className="role-modal__btn role-modal__btn--primary"
            disabled={isEmpty || loading}
            onClick={handleRoleSave}
          >
            {loading ? "Đang lưu…" : "Lưu vai trò"}
          </button>
        </div>
      }
    >
      {loadingRoles ? (
        <div className="role-modal__loading">
          <Spin />
        </div>
      ) : roleOptions.length === 0 ? (
        <p className="role-modal__hint">Chưa có vai trò nào trong hệ thống.</p>
      ) : (
        <>
          <ul className="role-modal__list">
            {roleOptions.map((role) => {
              const checked = selectedIds.includes(role.id);
              return (
                <li key={role.id}>
                  <Checkbox
                    className="role-modal__option"
                    checked={checked}
                    onChange={(event) => handleToggle(role.id, event.target.checked)}
                  >
                    {role.name}
                  </Checkbox>
                </li>
              );
            })}
          </ul>
          {/* Lỗi thay chỗ dòng gợi ý, không thêm dòng. */}
          <p className={`role-modal__hint${isEmpty ? " role-modal__hint--error" : ""}`} role={isEmpty ? "alert" : undefined}>
            {isEmpty
              ? "Chọn ít nhất 1 vai trò để lưu."
              : `Đã chọn ${selectedIds.length} vai trò. Mỗi tài khoản cần ít nhất 1 vai trò.`}
          </p>
        </>
      )}
    </Modal>
  );
}

export default AppUserDetailRoleModal;
